import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t.return || t.return(); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { v4 as uuid } from 'uuid';
import { ClientRegistry } from './client/bridge-api-client-registry';
import { client } from './client/bridge-api-client-types';
import { isBroadcastableCommand } from './command/bridge-api-command-guards';
import { CommandInvocation } from './command/bridge-api-command-invocation';
import { DiscoveryCommand, ReachableCommand, TeardownCommand } from './command/commands/discovery-commands';
import { destroyGlobalDebugConsumer } from './debug/bridge-api-debugger';
import { destroyGlobalEventEmitter, emitter } from './events/bridge-events-emitter';
import { BufferedInvocationQueue } from './resiliency/bridge-api-message-queue';
import { createInvocationId, isRPCRequest, isRPCResponse, rpcCommand } from './rpc/bridge-api-rpc-message';
import { RequestHandler } from './rpc/bridge-api-rpc-request-handler';
import { ResponseHandler } from './rpc/bridge-api-rpc-response-handler';
/* eslint-disable no-console */

/**
 * A bridge API which enables bi-directional communication between Rovo chat and immersive content.
 *
 * @description
 * Implements the following high level requirements to enable simple DevX with baked-in resiliency:
 *
 * 1) Uses the command pattern for strongly-typed invocation of methods
 * 2) Clients are able to register themselves as handling a specific command
 * 3) Clients can send command instances, with a `senderId` and `receiverId` for routing purposes
 * 4) If a client attempts to send a message to a client which is not ready yet, it will be buffered & tried again later; if
 * 		the recipient client takes too long to become ready, messages buffered agianst it will be dropped. See {@link BufferedInvocationQueue}.
 * 5) Clients exchange {@link DiscoveryCommand} and {@link ReachableCommand} to perform a 'connection handshake' on first attempt
 *  	at communicating with one another. This is to lazily establish context in bridge API instances around which clients a specific
 * 		instance can communicate with (point-to-point), rather than elaborate broadcasting schemes across JS contexts.
 * 6) Multiple command handlers can be implemented for a command; but, only one handler is allowed per command per clientID, to ensure
 * 		we can return the result of invoking a command with a specific `receiverId` without issue. See {@link ResponseHandler} for more.
 * 7) Commands are passed over-the-wire if crossing between JS contexts using the JSON RPC 2.0 protocol.
 * 		See {@link https://www.jsonrpc.org/specification} for more.
 *
 * @example
 * ```typescript
 * 	const api = getBrowserBridgeAPI();
 * 	const myApp = client('my-app');
 * 	const myChat = client('my-chat');
 *
 * 	// context 1 (my-chat) registers a handler
 * 	api.on(myChat, MyCommand, (params) => params.name.toUpperCase());
 *
 * 	// context 2 (my-app) sends the command
 * 	const invocation = api.send(MyCommand, {
 * 		name: 'my-app',
 * 		senderId: myApp,
 * 		receiverId: myChat
 * 	});
 *
 * 	// Get the result
 * 	const result = await invocation.result();
 * 	console.log(result); // expected: 'MY-APP'
 *
 * 	// Send a time-sensitive command that shouldn't be buffered
 * 	const urgentInvocation = api.send(UrgentCommand, {
 * 		senderId: myApp,
 * 		receiverId: myChat,
 * 		buffer: false // Will resolve to undefined if receiver not ready
 * 	});
 *
 * 	// Or cancel the command if needed
 * 	// invocation.cancel();
 * ```
 */
export var RovoContentBridgeAPI = /*#__PURE__*/function () {
  function RovoContentBridgeAPI(instanceId, transport) {
    var _this = this;
    _classCallCheck(this, RovoContentBridgeAPI);
    _defineProperty(this, "registry", new ClientRegistry());
    _defineProperty(this, "queue", new BufferedInvocationQueue());
    this.transport = transport;
    this.identifier = client(instanceId ? instanceId : "bridge-api-".concat(uuid()));
    this.rpc = {
      request: new RequestHandler(this.transport),
      response: new ResponseHandler()
    };

    // setup emitter for all events
    emitter().setBridgeId(this.identifier);

    // start listening for any incoming messages
    this.transport.onMessage(this.onMessage.bind(this));

    // setup tick handler for sending discovery commands to pending clients
    this.queue.onTick(function (pendingConnections) {
      var _iterator = _createForOfIteratorHelper(pendingConnections),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var _step$value = _step.value,
            senderId = _step$value.senderId,
            receiverId = _step$value.receiverId;
          _this.sendCommand(DiscoveryCommand, {
            senderId: senderId,
            receiverId: receiverId
          });
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    });

    // setup dropped message handler to cancel orphaned promises
    this.queue.onInvocationDroppped(function (m) {
      _this.rpc.response.cancel(m.invocationId);
      emitter().emit({
        type: 'message-dropped',
        messageCount: _this.queue.messageCount,
        messageId: m.invocationId,
        message: _objectSpread({}, m.params),
        clientId: m.senderId,
        reason: 'queue-overflow'
      });
    });

    // setup client added handler to drain commands when clients are ready
    this.registry.onClientAdded(function (clientId) {
      _this.drainCommands(clientId);
    });

    // observe readiness of bridge API
    emitter().emit({
      type: 'bridge-ready'
    });
  }

  /**
   * Unique ID for this specific bridge API instance.
   *
   * @description Should be the 1 instance in a given JS context.
   */
  return _createClass(RovoContentBridgeAPI, [{
    key: "id",
    get: function get() {
      return this.identifier;
    }

    /**
     * Indicates a specific client is ready to start sending and accepting messages.
     *
     * @param clientId unique identifier for the client.
     */
  }, {
    key: "ready",
    value: function ready(clientId) {
      this.registry.addLocalClient(clientId);

      // client-specific handlers - sent by a remote client -> this bridge API instance,
      // targeting the client which we are setting up in this method
      this.createDiscoveryHandler(clientId);
      this.createReachableHandler(clientId);
      this.createTeardownHandler(clientId);

      // emit client registration event
      emitter().emit({
        type: 'client-registered',
        clientId: clientId
      });
    }

    /**
     * Send a command with strongly-typed parameters to a recipient.
     *
     * @description It is possible to wait for the response of a command via the following:
     * ```typescript
     * const command = send(MyCommand, {});
     * await command.result(); // promise with result of the command
     * // optionally, to cancel:
     * command.cancel();
     * ```
     * Command invocations should be treated similarly in concept to network requests to an upstream system.
     * The upstream system can time out, disappear, crash, etc. Hence, all command results yield an optional
     * result, with resiliency baked in for connectivity with a client see {@link BufferedInvocationQueue}.
     *
     * Deferrables are used to expose a stable promise API for the result of a command. I.e., if the receiver
     * of a command is not seen as 'ready' yet, commands will be buffered until a time limit. Once that time
     * limit is expended, the message will be dropped, and its associated deferrable cancelled.
     *
     * @returns {CommandInvocation} api for handling the response of the command.
     */
  }, {
    key: "send",
    value: function send(command, params) {
      var _this2 = this;
      // assign a unique ID to this command invocation
      if (!params._invocationId) {
        params._invocationId = uuid();
      }

      // unique, stable ID for this command
      var id = createInvocationId(command, params);
      var message = rpcCommand(id, command, params);
      var senderId = params.senderId;
      var receiverId = params.receiverId;

      // emit command creation event
      emitter().emit({
        type: 'command-created',
        commandId: id,
        commandType: command.identifier,
        senderId: senderId,
        receiverId: receiverId,
        properties: _objectSpread({}, params),
        groupId: params.groupId
      });
      var promise;

      // not ready to send to a known, communicable client yet - buffer & defer (if allowed)
      if (!this.registry.has(receiverId) || !this.transport.canSend(message) || this.registry.isPendingClient(receiverId)) {
        var _params$buffer;
        // default to buffering the sending of all commands
        var buffer = (_params$buffer = params.buffer) !== null && _params$buffer !== void 0 ? _params$buffer : true;
        if (buffer) {
          this.queue.enqueue(id, senderId, receiverId, command, params);
          promise = this.rpc.response.getOrCreate(id, senderId, receiverId);
        } else {
          // opted out of buffering - resolve immediately to undefined
          promise = Promise.resolve(undefined);
        }
      }
      // ready to send to local client - immediately invoke
      else if (this.registry.isLocalClient(receiverId)) {
        // emit command sent event for local invocation
        emitter().emit({
          type: 'command-sent',
          commandId: id,
          commandType: command.identifier,
          senderId: senderId,
          receiverId: receiverId,
          properties: params,
          groupId: params.groupId
        });
        var result = this.rpc.request.invoke(command, params);
        var existing = this.rpc.response.get(id);

        // if a local client was not ready yet and we tried to communicate with it,
        // we do not want to instantiate a new promise. Hence, we need to latch onto
        // the same deferrable as before, and yield it with the result of invoking the
        // handler provided by the local client which is ready & we are communicating to
        if (existing) {
          existing.resolve(result);
          promise = existing.promise;
        } else {
          promise = result;
        }
      }
      // ready to send to remote client - use transport
      else {
        // emit command sent event
        emitter().emit({
          type: 'command-sent',
          commandId: id,
          commandType: command.identifier,
          senderId: senderId,
          receiverId: receiverId,
          properties: params,
          groupId: params.groupId
        });
        this.transport.send(message);
        promise = this.rpc.response.getOrCreate(id, senderId, receiverId);
      }
      return new CommandInvocation(id, promise, function (invocationId) {
        return _this2.cancel(invocationId);
      }, function (groupId) {
        return _this2.cancelGroup(groupId);
      });
    }

    /**
     * Broadcasts a command to all registered clients except those that are pending or filtered out.
     *
     * @typeParam C - The type of command properties, which must include a `receiverId` of type `ClientId`.
     * @param command - The constructor for the broadcastable command to be sent.
     * @param params - The parameters for the command, excluding the `receiverId` which will be set for each recipient.
     * @param options - Optional broadcast options, including a filter function to select specific clients.
     *
     * @remarks
     * - Only commands that pass `isBroadcastableCommand` are sent.
     * - The method retrieves all client IDs from the registry, filters out pending clients, and applies any additional filter provided in `options`.
     * - For each eligible client, the command is sent with the appropriate `receiverId`.
     * - Errors during broadcasting are caught and logged to the console.
     */
  }, {
    key: "broadcast",
    value: function broadcast(command, params, options) {
      var _this3 = this;
      try {
        var _this$registry$getAll, _this$registry$getAll2;
        if (!isBroadcastableCommand(command)) {
          return;
        }
        var receiverIds = (_this$registry$getAll = (_this$registry$getAll2 = this.registry.getAllClientIds()) === null || _this$registry$getAll2 === void 0 || (_this$registry$getAll2 = _this$registry$getAll2.filter(function (clientId) {
          return !_this3.registry.isPendingClient(clientId) && String(params.senderId) !== clientId;
        })) === null || _this$registry$getAll2 === void 0 ? void 0 : _this$registry$getAll2.filter(function (clientId) {
          return options !== null && options !== void 0 && options.filter ? options.filter(clientId) : true;
        })) !== null && _this$registry$getAll !== void 0 ? _this$registry$getAll : [];
        var _iterator2 = _createForOfIteratorHelper(receiverIds),
          _step2;
        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var receiverId = _step2.value;
            this.send(command, _objectSpread(_objectSpread({}, params), {}, {
              receiverId: receiverId
            }));
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }
        if (receiverIds.length) {
          emitter().emit({
            type: 'command-broadcast',
            senderId: params.senderId,
            receiverIds: receiverIds,
            commandType: command.identifier
          });
        }
      } catch (err) {
        console.error('broadcast failed', err);
      }
    }

    /**
     * Register a handler for a specific command type for a specific client.
     */
  }, {
    key: "on",
    value: function on(clientId, Command, handler) {
      this.rpc.request.register(clientId, Command, handler);
      emitter().emit({
        type: 'handler-registered',
        clientId: clientId,
        commandType: Command.identifier
      });
    }

    /**
     * Unregister a handler for a specific command type for a specific client.
     */
  }, {
    key: "off",
    value: function off(clientId, Command) {
      this.rpc.request.unregister(clientId, Command);
      emitter().emit({
        type: 'handler-unregistered',
        clientId: clientId,
        commandType: Command.identifier
      });
    }

    /**
     * Tears down a specific client, removing it from the registry and notifying all remote clients.
     *
     * @description This method performs the following actions:
     * 1. Removes the client from the local registry
     * 2. Sends teardown commands to all known remote clients
     * 3. Drops all buffered messages for the client
     * 4. Unregisters all command handlers for the client
     * 5. Clears any pending response handlers for the client
     *
     * @param clientId unique identifier for the client to tear down
     */
  }, {
    key: "teardown",
    value: function teardown(clientId) {
      // 1. remove the client from the registry
      var wasRemoved = this.registry.removeClient(clientId);
      if (!wasRemoved) {
        return;
      }

      // 2. notify all remote clients about the teardown
      var allClientIds = this.registry.getAllClientIds();
      var _iterator3 = _createForOfIteratorHelper(allClientIds),
        _step3;
      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var otherClientId = _step3.value;
          // irrespective of state, we should communicate we are tearing down
          // this handles any potential race conditions with in-flight messages
          // across JS contexts (e.g., actively shaking hands with a pending client)
          if (this.registry.isPendingClient(otherClientId) || this.registry.isRemoteClient(otherClientId)) {
            this.sendCommand(TeardownCommand, {
              senderId: clientId,
              receiverId: otherClientId
            });
          }
        }

        // 3. drop all buffered messages for this client
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
      this.queue.clear(clientId);

      // 4. unregister all command handlers for this client
      this.rpc.request.unregisterAll(clientId);

      // 5. clear any pending response handlers for this client
      this.rpc.response.teardown(clientId);

      // 6. emit client teardown event
      emitter().emit({
        type: 'client-teardown',
        clientId: clientId
      });
    }

    /**
     * Clear all state prior to the application being destroyed.
     *
     * @description NOTE: Ensure all containing state is destroyed to avoid memory leaks during
     * SPA transitions away from application contexts which use the bridge API.
     */
  }, {
    key: "destroy",
    value: function destroy() {
      this.queue.destroy();
      this.rpc.request.destroy();
      this.rpc.response.clear();
      this.registry.destroy();
      this.transport.destroy();

      // stop emitting any events & drop memory
      emitter().emit({
        type: 'bridge-destroyed'
      });

      // clean up all event emitter state
      destroyGlobalEventEmitter();
      destroyGlobalDebugConsumer();
    }

    /**
     * Registers a message listener for RPC events received over {@link transport}.
     * @param data RPC message received by this JS context
     */
  }, {
    key: "onMessage",
    value: (function () {
      var _onMessage = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(data) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              if (!isRPCRequest(data)) {
                _context.next = 5;
                break;
              }
              _context.next = 4;
              return this.rpc.request.handleRequest(data);
            case 4:
              return _context.abrupt("return", _context.sent);
            case 5:
              if (!isRPCResponse(data)) {
                _context.next = 7;
                break;
              }
              return _context.abrupt("return", this.rpc.response.resolveWithResponse(data));
            case 7:
              _context.next = 12;
              break;
            case 9:
              _context.prev = 9;
              _context.t0 = _context["catch"](0);
              console.error('failed to handle message:', _context.t0);
            case 12:
            case "end":
              return _context.stop();
          }
        }, _callee, this, [[0, 9]]);
      }));
      function onMessage(_x) {
        return _onMessage.apply(this, arguments);
      }
      return onMessage;
    }()
    /**
     * Sets up a reachable handler for a specific client ID
     * @summary the {@link senderId} is the remote client who sent this command, expressing they are now reachable.
     * @param senderId
     */
    )
  }, {
    key: "createReachableHandler",
    value: function createReachableHandler(senderId) {
      var _this4 = this;
      this.on(senderId, ReachableCommand, function (params) {
        _this4.registry.addRemoteClient(params.senderId);
        emitter().emit({
          type: 'client-discovered',
          clientId: params.senderId,
          metadata: {
            discoveryMethod: 'reachable-command',
            localClientId: senderId
          }
        });
      });
    }

    /**
     * Sets up a discoverable handler for a specific client ID
     * @summary the {@link senderId} is the remote client who sent this command, expressing they are looking for {@link senderId}.
     * @param senderId
     */
  }, {
    key: "createDiscoveryHandler",
    value: function createDiscoveryHandler(senderId) {
      var _this5 = this;
      this.on(senderId, DiscoveryCommand, function (params) {
        _this5.sendCommand(ReachableCommand, {
          senderId: params.receiverId,
          receiverId: params.senderId
        });
        _this5.registry.addRemoteClient(params.senderId);
        emitter().emit({
          type: 'client-discovered',
          clientId: params.senderId,
          metadata: {
            discoveryMethod: 'discovery-command',
            localClientId: senderId
          }
        });
      });
    }

    /**
     * Sets up a teardown handler for a specific client ID
     * @summary the {@link senderId} is the remote client who sent this command, expressing they are shutting down.
     * @param receiverId the local client that should handle teardown notifications
     */
  }, {
    key: "createTeardownHandler",
    value: function createTeardownHandler(receiverId) {
      var _this6 = this;
      this.on(receiverId, TeardownCommand, function (params) {
        // remove the client that is tearing down from our registry
        _this6.registry.removeClient(params.senderId);

        // drop all buffered messages for the client that is tearing down
        _this6.queue.clear(params.senderId);
      });
    }

    /**
     * Immediately send a command over the configured {@link transport}.
     *
     * @description Used for handshake between clients, to simplify logic.
     * @description Re-used to send all commands in the bridge API.
     *
     * @param command command to send
     * @param params command properties to provide
     */
  }, {
    key: "sendCommand",
    value: function sendCommand(command, params) {
      var id = createInvocationId(command, params);
      var message = rpcCommand(id, command, params);
      this.transport.send(message);
    }

    /**
     * Drain commands for a given client ID from the queue.
     * @param clientId unique identifier for the client
     */
  }, {
    key: "drainCommands",
    value: function drainCommands(clientId) {
      var messages = this.queue.flush(clientId);
      if (!messages) {
        return;
      }
      var _iterator4 = _createForOfIteratorHelper(messages),
        _step4;
      try {
        for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
          var message = _step4.value;
          this.send(message.command, message.params);
        }
      } catch (err) {
        _iterator4.e(err);
      } finally {
        _iterator4.f();
      }
    }

    /**
     * Cancel all commands in a specific group.
     *
     * @param groupId The group identifier of commands to cancel
     * @returns number of commands that were cancelled
     */
  }, {
    key: "cancelGroup",
    value: function cancelGroup(groupId) {
      // remove the buffered commands with this group ID from the queue, if they exist; will also clean up
      // the RPC response handler deferrable for each command
      return this.queue.cancelGroup(groupId);
    }

    /**
     * Cancel a specific command invocation by its ID.
     *
     * @param invocationId The unique identifier of the command invocation to cancel
     * @returns true if the command was found and cancelled, false otherwise
     */
  }, {
    key: "cancel",
    value: function cancel(invocationId) {
      // remove the buffered command from the queue, if it exists on it; this will also clean up
      // the RPC response handler deferrable for this command
      return this.queue.cancel(invocationId);
    }
  }]);
}();